import * as Sentry from '@sentry/vue';
import { createSentryPiniaPlugin } from '@sentry/vue';
import posthog from 'posthog-js';
import type { App } from 'vue';
import type { Router } from 'vue-router';
// The same reader the UI uses to turn an API refusal into a sentence. Pure, no
// imports of its own, so pulling it in here costs nothing and cannot cycle.
import { fallbackMessage } from '@/utils/errorEnvelope';

/**
 * Collect comprehensive browser/device metadata
 */
function getBrowserMetadata(): Record<string, any> {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  const perf = performance as any;

  const meta: Record<string, any> = {
    // Browser
    user_agent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(', '),
    platform: nav.platform || nav.userAgentData?.platform || 'unknown',
    vendor: nav.vendor || 'unknown',
    cookie_enabled: navigator.cookieEnabled,
    do_not_track: nav.doNotTrack,
    // Screen
    screen_width: screen.width,
    screen_height: screen.height,
    screen_color_depth: screen.colorDepth,
    screen_pixel_depth: screen.pixelDepth,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
    // Orientation
    orientation: screen.orientation?.type || 'unknown',
    // Timezone & locale
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: new Date().getTimezoneOffset(),
    locale: Intl.DateTimeFormat().resolvedOptions().locale,
    // Connection
    online: navigator.onLine,
    connection_type: conn?.effectiveType || 'unknown',
    connection_downlink: conn?.downlink || 'unknown',
    connection_rtt: conn?.rtt || 'unknown',
    connection_save_data: conn?.saveData || false,
    // Hardware
    cpu_cores: navigator.hardwareConcurrency || 'unknown',
    device_memory: nav.deviceMemory ? `${nav.deviceMemory} GB` : 'unknown',
    max_touch_points: navigator.maxTouchPoints || 0,
    // Storage
    local_storage_available: !!window.localStorage,
    session_storage_available: !!window.sessionStorage,
    indexed_db_available: !!window.indexedDB,
    // PDF/media support
    pdf_viewer_enabled: nav.pdfViewerEnabled ?? 'unknown',
    // Page
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_referrer: document.referrer || 'direct',
    page_title: document.title,
  };

  // Performance memory (Chrome only)
  if (perf.memory) {
    meta.js_heap_size_limit = `${Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024)} MB`;
    meta.js_heap_used = `${Math.round(perf.memory.usedJSHeapSize / 1024 / 1024)} MB`;
    meta.js_heap_total = `${Math.round(perf.memory.totalJSHeapSize / 1024 / 1024)} MB`;
  }

  // Navigation timing
  if (perf.getEntriesByType) {
    const navEntry = perf.getEntriesByType('navigation')[0] as any;
    if (navEntry) {
      meta.page_load_time = Math.round(navEntry.loadEventEnd - navEntry.startTime);
      meta.dom_content_loaded = Math.round(navEntry.domContentLoadedEventEnd - navEntry.startTime);
      meta.dns_lookup = Math.round(navEntry.domainLookupEnd - navEntry.domainLookupStart);
      meta.tcp_connect = Math.round(navEntry.connectEnd - navEntry.connectStart);
      meta.ttfb = Math.round(navEntry.responseStart - navEntry.requestStart);
      meta.navigation_type = navEntry.type; // navigate, reload, back_forward, prerender
    }
  }

  return meta;
}

export let sentryPiniaPlugin: ReturnType<typeof createSentryPiniaPlugin> | null = null;

export function initMonitoring(app: App, router: Router) {
  // Enable monitoring in production and staging (disabled on local dev)
  // Staging should use a separate PostHog project key so it does not pollute prod analytics.
  const mode = import.meta.env.MODE;
  if (mode !== 'production' && mode !== 'staging') {
    return;
  }

  // Initialize Sentry (production only — staging should not report to Sentry)
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  if (mode === 'production' && sentryDsn && sentryDsn !== 'your_sentry_dsn_here') {
    const browserMeta = getBrowserMetadata();

    Sentry.init({
      app,
      dsn: sentryDsn,
      debug: false,
      integrations: [
        // ── Performance tracing with route instrumentation ──
        Sentry.browserTracingIntegration({
          router,
          enableInp: true,
        }),
        // ── Session replay: capture everything unmasked ──
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          // Capture full network request/response in replay
          networkDetailAllowUrls: [/builtsimple\.com\.au/, /localhost/, /127\.0\.0\.1/],
          // ...minus the responses that are too big to be worth a copy of.
          //
          // A matched response is read IN FULL into a JavaScript string and only
          // THEN truncated to 150 KB (NETWORK_BODY_MAX_SIZE) — and the read runs
          // on `response.clone()`, so the bytes are held twice. Keeping 150 KB of
          // a plan PDF, an XFDF annotation blob or a takeoff payload therefore
          // costs the whole thing, on the two pages that have the least headroom
          // to spare. BS-WEB-APP-173 is an unhandled Firefox "out of memory" on
          // /estimate-list and /ncc-assistant with no stack of its own.
          //
          // The AI streams are a worse case than merely big. A chunked response
          // carries no Content-Length, and with the size unknown replay's fetch
          // path clones and reads it regardless of these lists
          // (@sentry-internal/replay 10.42.0, _getResponseInfo) — for the entire
          // life of the stream, which is however long the model takes to answer.
          // /v1/chat is the Mat AI bubble, mounted by FullLayout on EVERY page,
          // which is how one cause reaches both URLs on this issue. Denying them
          // takes effect on the XHR path today and on the fetch path the moment
          // a release honours it there; the listing is also the record of why
          // they must not be captured.
          networkDetailDenyUrls: [
            /\/v1\/chat/,
            /\/v1\/ncc\/stream/,
            /\/v1\/transcribe/,
            /\/storage\//,
            /\/viewannotation/,
            /\/addannotate/,
            /\/(download|export)/i,
            /\.(pdf|dxf|dwg|zip|csv|xlsx?|png|jpe?g|webp)(\?|$)/i,
          ],
          networkCaptureBodies: true,
          networkRequestHeaders: ['X-Requested-With', 'Content-Type', 'Authorization', 'Accept'],
          networkResponseHeaders: ['Content-Type', 'X-Request-Id', 'X-RateLimit-Remaining'],
        }),
        // ── Replay canvas: capture charts, signatures, canvas elements ──
        Sentry.replayCanvasIntegration(),
        // ── Breadcrumbs: all DOM, console, fetch, XHR, navigation ──
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: { serializeAttribute: ['data-testid', 'data-id', 'id', 'class', 'aria-label'] },
          fetch: true,
          history: true,
          xhr: true,
        }),
        // ── HTTP client: auto-capture 4xx/5xx as events ──
        // Exclude socket.io endpoints from HTTP client error tracking
        Sentry.httpClientIntegration({
          failedRequestStatusCodes: [[400, 421], [423, 599]],
          failedRequestTargets: [
            /builtsimple\.com\.au\/(?!socket\.io)/,  // Match builtsimple.com.au but exclude /socket.io
            /localhost\/(?!socket\.io)/,
            /127\.0\.0\.1\/(?!socket\.io)/,
          ],
        }),
        // ── Console capture: error + warn as Sentry events ──
        Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
        // ── Browser profiling: flame charts for slow pages ──
        Sentry.browserProfilingIntegration(),
        // ── Context lines: show source code around errors ──
        Sentry.contextLinesIntegration({ frameContextLines: 7 }),
        // ── Extra error data: capture non-standard Error properties ──
        Sentry.extraErrorDataIntegration({ depth: 5 }),
        // ── Reporting observer: browser deprecation/intervention reports ──
        Sentry.reportingObserverIntegration({ types: ['crash', 'deprecation', 'intervention'] }),
        // ── Culture context: locale, timezone, calendar info ──
        Sentry.cultureContextIntegration(),
        // ── HTTP context: browser, OS, device from user-agent ──
        Sentry.httpContextIntegration(),
      ],

      // ── Sampling: capture everything ──
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
      profilesSampleRate: import.meta.env.PROD ? 0.5 : 1.0,

      // ── PII & data ──
      sendDefaultPii: true,
      attachStacktrace: true,

      // ── Breadcrumbs ──
      maxBreadcrumbs: 250,

      // ── Environment ──
      environment: import.meta.env.MODE,
      // VITE_APP_VERSION still wins so an explicit value can override, but it
      // is set nowhere -- __BUILD_VERSION__ is what actually fills this in, and
      // is why events now carry a release at all.
      release: import.meta.env.VITE_APP_VERSION || __BUILD_VERSION__ || undefined,

      // ── Tags for filtering ──
      initialScope: {
        tags: {
          app: 'builtsimple-web',
          browser: browserMeta.vendor || 'unknown',
          connection_type: browserMeta.connection_type,
          cpu_cores: String(browserMeta.cpu_cores),
          device_memory: browserMeta.device_memory,
        },
      },

      // ── Enrich every event with full metadata ──
      beforeSend(event) {
        // Extract common values used across filters
        const status = event.tags?.api_status || event.contexts?.api_response?.status;
        const message = event.message || event.exception?.values?.[0]?.value || '';
        const url = event.contexts?.trace?.data?.url || event.request?.url || '';
        if (status == '422' || status == 422 || message.includes('API POST 422') ||
            message.includes('API GET 422') || message.includes('API PUT 422') ||
            message.includes('API DELETE 422')) {
          return null;
        }

        // Skip 404 errors - these are "not found" errors that are expected application behavior
        if (status == '404' || status == 404 || message.includes('status code: 404')) {
          return null;
        }

        // Skip 413 Payload Too Large. Same class as 422: the user picked a file
        // bigger than the server accepts, the API says so in plain words and the
        // UI shows it. Nothing to fix in code. Worth stating explicitly because
        // this status only started appearing once EnforceUploadSize began
        // answering oversize uploads honestly — before that they surfaced as a
        // misleading 400 "No files uploaded".
        if (status == '413' || status == 413 || message.includes('status code: 413') ||
            message.includes('API POST 413') || message.includes('API PUT 413')) {
          return null;
        }

        // Skip 409 Conflict - expected "already exists"/duplicate validation that the
        // backend rejects and we surface to the user via a toast (e.g. "A work type
        // with name 'X' already exists"). Same class as 422 — not an actionable bug.
        if (status == '409' || status == 409 ||
            message.includes('API POST 409') || message.includes('API GET 409') ||
            message.includes('API PUT 409') || message.includes('API DELETE 409') ||
            message.includes('status code: 409')) {
          return null;
        }

        // Skip 522/523/524 errors - these are Cloudflare timeout/origin errors (server-side issues)
        if (status == '522' || status == 522 || status == '523' || status == 523 || status == '524' || status == 524) {
          return null;
        }

        // Shared exception metadata used by several filters below.
        const mechType = event.exception?.values?.[0]?.mechanism?.type || '';
        const exceptionType = event.exception?.values?.[0]?.type || '';
        const responseStatus = (event.contexts as any)?.response?.status_code;

        // Browser out of memory — the engine threw, not our code.
        //
        // Firefox reports an OOM as a bare "out of memory" with NO stack of its
        // own, so the SDK attaches a synthetic one and every frame in it belongs
        // to @sentry/core. The event names no page, no request and no line:
        // BS-WEB-APP-173 is two events from two users that between them say only
        // that a tab ran out of heap, one on /estimate-list and one on
        // /ncc-assistant.
        //
        // NOT dropped. Every other filter here removes a DUPLICATE — signal that
        // survives somewhere better (captureApiError, the Vue integration, a
        // toast the user already saw). A tab exhausting its heap has no
        // elsewhere, and it is the most user-visible failure there is.
        //
        // What it gets instead is a label and a fingerprint BY ROUTE, so one
        // group per page answers "which page allocates" — the question a
        // stackless OOM otherwise cannot be asked. Falls through deliberately:
        // the enrichment at the end of this function is what attaches
        // memory_state (heap used / limit / pct, where the browser exposes it),
        // hardware.device_memory and runtime_state.app_uptime_ms, and uptime is
        // what separates a slow leak from one allocation that blew the roof off.
        // Firefox — the browser this actually happens in — exposes neither
        // performance.memory nor navigator.deviceMemory, so on these very events
        // that context is thin; it is not thin on Chrome, and the route is the
        // part that always lands.
        if (/^(out of memory|allocation size overflow|array buffer allocation failed)/i.test(message.trim())) {
          const oomRoute = event.transaction || window.location.pathname || 'unknown';
          event.tags = { ...event.tags, error_type: 'browser_oom', oom_route: oomRoute };
          event.fingerprint = ['browser-oom', oomRoute];
        }

        // The analytics tripwire in main.ts states one FACT about the build —
        // this production bundle carries no GA4 measurement id, so no page view
        // and no Ads conversion will ever be sent. It is deliberately a
        // console.error so captureConsoleIntegration reports it; see main.ts.
        //
        // But captureConsole fingerprints on the STACK, and the stack is
        // minified application code whose file hash and symbol names change with
        // every build. So one unchanging misconfiguration opened a fresh issue
        // per deploy, each titled by whatever the minifier called the enclosing
        // function: BS-WEB-APP-16Q (514 events, 242 users) and BS-WEB-APP-16T
        // ("bY", 2 events) are the same sentence about the same missing variable.
        // Splitting it that way also hides how long it has been true.
        //
        // Pinned to one fingerprint: the CI fix (ci-cd.yml exports the id for the
        // production deploy and asserts it reached dist/) means this should now
        // stay silent, and if it ever comes back it lands on ONE issue, marked as
        // a regression, instead of under an id nobody recognises.
        if (message.includes('[analytics] VITE_GA4_MEASUREMENT_ID is not set')) {
          event.fingerprint = ['analytics-ga4-id-missing'];
          event.tags = { ...event.tags, error_type: 'build_misconfiguration' };
        }

        // A non-Error value thrown where nothing can describe it.
        //
        // Firefox words a non-Error throw as "uncaught exception: <value>", and
        // BS-WEB-APP-166 is that sentence with the value spelled `undefined` —
        // one event, one Firefox Mobile user, on /register. Sentry's only frame
        // is the document url, captioned "No additional details are available
        // for this frame": no line, no column, no function, because
        // window.onerror was handed none.
        //
        // That combination — the engine's non-Error wording, nothing inside the
        // value, and not one frame carrying a position — is a throw from code
        // the browser will not describe to the page: an extension content
        // script, or the browser's own injected code. Firefox for Android runs
        // both, and neither is ours to fix.
        //
        // The frame test is why this is narrow enough to be safe. A throw from
        // our own bundle reaches onerror WITH lineno and colno even when the
        // thrown value is undefined, so it keeps reporting; only the opaque
        // case is dropped. "Script error." is the same situation seen through
        // the cross-origin rule, so it belongs in the same condition.
        // "Does this event point at any code?" — asked by more than one branch
        // below. A frame with neither a line number nor a function name is a
        // placeholder, not a location.
        const eventFrames = event.exception?.values?.[0]?.stacktrace?.frames || [];
        const hasLocatableFrame = eventFrames.some(
          (frame) => typeof frame.lineno === 'number' || !!frame.function
        );
        if (mechType === 'auto.browser.global_handlers.onerror' &&
            /^(uncaught exception: (undefined|null)|script error\.?)$/i.test(message.trim()) &&
            !hasLocatableFrame) {
          return null;
        }

        // Skip auto-captured HTTP failures from httpClientIntegration. The fetch
        // wrapper already routes API failures through captureApiError() with full
        // context (request body, response body, network state), so these are pure
        // duplicates. We drop:
        //   • 401/403 — auth handled by the axios interceptor (user redirected to
        //     login); the auto-event carries nothing actionable
        //   • 5xx — backend issues already captured with full context elsewhere
        if (mechType.startsWith('auto.http.client') &&
            typeof responseStatus === 'number' &&
            (responseStatus === 401 || responseStatus === 403 ||
             (responseStatus >= 500 && responseStatus < 600))) {
          return null;
        }

        // Everything else from httpClientIntegration (the 4xx band) is ALSO a
        // duplicate of captureApiError — but we can't just drop it, because a few
        // call sites use raw fetch()/raw axios and never reach captureApiError, so
        // the auto-event is their only report.
        //
        // Instead, give it captureApiError's fingerprint — [method, url, status] —
        // so Sentry FILES IT UNDER THE SAME ISSUE rather than opening a parallel
        // one. One failed upload was producing three issues: this auto-event
        // ("HTTP Client Error with status code: 400"), the captureApiError event
        // ("API POST 400: <url>"), and a captureConsole copy of the caller's
        // console.error. Same incident, same trace id, three groups to triage.
        // Aligning the fingerprint collapses them and loses no coverage.
        if (mechType.startsWith('auto.http.client') && typeof responseStatus === 'number') {
          const failedUrl = event.request?.url || url;
          const failedMethod = (event.request?.method || event.tags?.api_method || 'GET') as string;
          if (failedUrl) {
            event.fingerprint = [failedMethod, failedUrl, String(responseStatus)];
          }
        }

        // Skip captureConsoleIntegration's wrapping of a request that failed.
        //
        // Two kinds land here. The ones with a status code are duplicates: we
        // surface them to the user via toasts and capture them with full context
        // through captureApiError() in the fetch wrapper, so the captured-console
        // copy adds nothing.
        //
        // The ones WITHOUT a status code — a fetch that never reached the server
        // — have no captureApiError copy at all, because both axios.ts and the
        // fetch wrapper only call it when there is a response to report. That is
        // deliberate rather than an oversight: a request that died in transit is
        // a dropped connection, a sleeping laptop, or a navigation away
        // mid-flight. There is no status, no response, and (as the issue shows)
        // no stack — nothing an engineer can act on, and nothing that was ours.
        //
        // Every engine words that case differently, and the list below only ever
        // knew axios's spelling, "Network Error" WITH A SPACE. Firefox says
        // "NetworkError when attempting to fetch resource." — no space, no match
        // — so it went straight through as an unhandled-looking TypeError:
        // BS-WEB-APP-16H, two events from two Firefox users on /estimate-list,
        // both the cosmetic company-logo fetch in EstimateList/index.js whose
        // catch exists precisely to let the page carry on without it.
        if (mechType === 'auto.core.capture_console' &&
            (/AxiosError/i.test(message) ||
             /AxiosError/i.test(exceptionType) ||
             /Network Error/i.test(message) ||
             /Network Error/i.test(exceptionType) ||
             /ERR_NETWORK/i.test(message) ||
             /ECONNABORTED/i.test(message) ||
             /timeout of \d+ms exceeded/i.test(message) ||
             /Request failed with status code \d+/i.test(message) ||
             // Native fetch, failing before a response exists.
             /NetworkError when attempting to fetch resource/i.test(message) ||  // Firefox
             /Failed to fetch/i.test(message) ||                                 // Chrome / Edge
             /The network connection was lost/i.test(message) ||                 // Safari, iOS
             /The Internet connection appears to be offline/i.test(message) ||   // Safari, iOS
             // Safari's other wording is two ordinary words, so it is only
             // trusted next to the type every engine throws for a dead fetch —
             // otherwise "Plan load failed" and friends would be swallowed too.
             (exceptionType === 'TypeError' && /\bLoad failed\b/i.test(message)))) {
          return null;
        }

        // An abort nobody was left to hear.
        //
        // BS-WEB-APP-16G: "AbortError: The operation was aborted.", an unhandled
        // rejection with NO stack trace at all, one Firefox user on
        // /estimate-list. An AbortError means something cancelled an operation
        // on purpose — a navigation ending an in-flight request, a viewer
        // closing and dropping its own fetch, a timeout firing. Nothing was
        // broken by it; the request simply stopped being wanted.
        //
        // The frame test carries the same weight it does for the onerror branch
        // above. An abort our own code leaves unhandled arrives with a stack
        // pointing at it, and keeps reporting — which matters, because an
        // unhandled abort in our code CAN leave a spinner running or a panel
        // half-loaded. Only the version with nothing to point at is dropped,
        // and that one is a browser or a third-party library tidying up after
        // itself: /estimate-list loads the plan viewer, which cancels its own
        // requests, and has no uncaught AbortError path of its own.
        if ((mechType === 'auto.browser.global_handlers.onunhandledrejection' ||
             event.tags?.error_type === 'unhandled_promise_rejection') &&
            (exceptionType === 'AbortError' || /^The operation was aborted\.?$/i.test(message.trim())) &&
            !hasLocatableFrame) {
          return null;
        }

        // Vue's default errorHandler does `console.error('[Vue Error] ...', err)`,
        // which captureConsoleIntegration then ships as its own Sentry event.
        // The Sentry Vue integration already captures the same error with proper
        // mechanism metadata (component, lifecycle, props) — the captured-console
        // copy is duplicate noise with a worse title.
        if (mechType === 'auto.core.capture_console' &&
            /\[Vue Error\]/i.test(message)) {
          return null;
        }

        // The monitoring stack warning about itself.
        //
        // captureConsoleIntegration listens at 'warn', so a console.warn from
        // ANY library becomes an issue — including the Sentry SDK's own. Each
        // of these is emitted from inside a try/catch that has already chosen a
        // fallback and carried on:
        //
        //   "Unable to initialize CanvasManager"  -> CanvasManagerNoop(); the
        //       replay still records, just without canvas. BS-WEB-APP-174,
        //       one Chrome/Linux user on /login.
        //   "Failed to snapshot the document"     -> the snapshot is skipped.
        //   "Error filtering CSS properties:"     -> the unfiltered cssText.
        //
        // Nothing in the app failed, and nothing in the app can act on them:
        // they are the recorder reporting on the recorder. They read as ours
        // only because the captureConsole stack bottoms out in initMonitoring(),
        // where Sentry.init() was called — which is why the issue is TITLED
        // "initMonitoring" and looks, at a glance, like our boot code broke.
        //
        // Matched on the message because that is the only handle: the SDK emits
        // them through plain console.warn, with no marker of its own, and by the
        // time beforeSend sees the event the frames are a mix of SDK and app.
        if (mechType === 'auto.core.capture_console' &&
            (/^Unable to initialize CanvasManager/i.test(message) ||
             /^Failed to snapshot the document/i.test(message) ||
             /^Error filtering CSS properties/i.test(message) ||
             /^Sentry Logger /i.test(message))) {
          return null;
        }

        // `console.error('Something failed:', err)` where err is a PLAIN OBJECT.
        // The fetch wrapper rejects with the parsed response BODY rather than an
        // Error, so every such call stringifies to "...: [object Object]" and
        // captureConsoleIntegration files it as an error-level issue whose entire
        // content is the label the developer happened to type. An Error would
        // render as "Error: <message>", so this pattern can only match the
        // contentless case.
        //
        // These have been converted to logApiError() file by file — Dashboard,
        // Costings, ScheduleJobs — but there are still ~800 console.error call
        // sites that pass a caught value straight through, and each new one opens
        // a fresh issue naming nothing. The HTTP failure behind it is already
        // reported by captureApiError() with URL, status, request and response
        // body, so dropping the console copy loses no coverage: it removes a
        // duplicate that cannot, by construction, say what went wrong.
        // BS-WEB-APP-15S / 1SR / 15Q / 16N were all this one shape.
        if (mechType === 'auto.core.capture_console' && /\[object Object\]/.test(message)) {
          return null;
        }

        // localStorage/sessionStorage full — QuotaExceededError from caching a
        // large object (e.g. a tender with embedded proposal images) to
        // localStorage. It's caught and handled (the API is the source of
        // truth); the write is retried after pruning, then skipped. Backstop in
        // case any other path console.errors it — not an actionable crash.
        if (/QuotaExceededError/i.test(exceptionType) ||
            /QuotaExceededError/i.test(message) ||
            /NS_ERROR_DOM_QUOTA_REACHED/i.test(message) ||
            /exceeded the quota/i.test(message)) {
          return null;
        }

        // Chunked-upload backend errors surfaced via console.error from
        // ImportWizard/UploadProgress.vue. The chunked uploader uses raw axios
        // (not fetch-wrapper), so these don't go through captureApiError —
        // they're only visible via captureConsoleIntegration. The Lstat /
        // SplFileInfo::getType() failure is a known backend issue (chunk merge
        // path not found) tracked separately; per-occurrence Sentry events
        // aren't actionable on the frontend.
        if (/SplFileInfo::getType\(\): Lstat failed/i.test(message) ||
            (/Upload error:/i.test(message) && /\/storage\/app\/public\/chunks\//i.test(message))) {
          return null;
        }

        // Google Maps deprecation warnings (console.warn from the Maps SDK).
        // We can't fix these without migrating off the deprecated APIs, and they
        // fire on every page that mounts a map — pure noise in Sentry until the
        // migration ships. Tracked separately; un-filter once migrated.
        if (/google\.maps\.Marker is deprecated/i.test(message) ||
            /google\.maps\.marker\.AdvancedMarkerElement/i.test(message) ||
            /google\.maps\.places\.AutocompleteService/i.test(message) ||
            /google\.maps\.places\.Autocomplete is not available to new customers/i.test(message) ||
            /AutocompleteService is not available to new customers/i.test(message) ||
            /Google Maps already loaded outside @googlemaps\/js-api-loader/i.test(message)) {
          return null;
        }

        // Google Identity Services narrating its own normal cancellation.
        //
        // /login calls google.accounts.id.prompt() for One Tap. The FedCM
        // request underneath it is aborted whenever the prompt ends without a
        // credential — the visitor dismisses it (cancel_on_tap_outside is on by
        // our own configuration), signs in with the password form instead so the
        // component unmounts, or simply navigates away. Google's library logs
        // that at ERROR level, and captureConsoleIntegration files it:
        // BS-WEB-APP-16Z, titled "xY" after whatever the minifier called the
        // enclosing function.
        //
        // Nothing failed. initOneTap() is written to be silent on every failure
        // path precisely so the login form someone came here to use is never
        // disturbed, and it is not disturbed here either.
        //
        // Scoped to the ABORT wordings, not to [GSI_LOGGER] as a whole. The
        // library also reports real misconfiguration through the same channel —
        // "The given origin is not allowed for the given client ID" means One
        // Tap is silently dead for everybody, which is exactly the thing worth
        // being told about. That keeps reporting.
        if (mechType === 'auto.core.capture_console' &&
            /^\[GSI_LOGGER\]/i.test(message.trim()) &&
            /(AbortError|signal is aborted|aborted without reason)/i.test(message)) {
          return null;
        }

        // Skip CORS wildcard deprecation warnings from browser ReportingObserver
        // This is a Chrome deprecation notice about future CORS behavior, not our bug
        // This is a backend/API server configuration issue, not a frontend code issue
        if (message.includes('Authorization will not be covered by the wildcard symbol') ||
            message.includes('Access-Control-Allow-Headers') ||
            message.includes('ReportingObserver [deprecation]') ||
            (message.includes('CORS') && message.includes('wildcard')) ||
            (message.includes('Authorization') && message.includes('CORS'))) {
          return null;
        }

        // Skip document policy violations - browser security policy warnings
        if (message.includes('Document policy violation') || message.includes('js-profiling is not allowed')) {
          return null;
        }

        // Third-party @pdftron/webviewer (Apryse) internal UI errors. Its
        // React-based viewer UI (webviewer-ui.min.js) intermittently hits a null
        // ref while measuring an element during layout/unmount — e.g.
        // "null is not an object (n.current.getBoundingClientRect)", Safari-only,
        // handled. It's a library-internal race we can't fix in app code. No app
        // code accesses `.current.getBoundingClientRect` (verified), so any such
        // error — or anything originating in the WebViewer bundle — is the
        // viewer's, not ours. Drop as un-actionable noise.
        const frames = (event.exception?.values || []).flatMap(
          (v: any) => v?.stacktrace?.frames || []
        );
        if (/\.current\.getBoundingClientRect/i.test(message) ||
            frames.some((f: any) => /webviewer|pdftron|pdfjs-express/i.test(f?.filename || f?.abs_path || ''))) {
          return null;
        }

        // WebViewer library console warnings (captured via captureConsole), e.g.
        // "[WebViewer] Version Mismatch: Core and UI should be updated to version
        // X". These are emitted by the library itself and reflect an asset/version
        // drift to fix during a WebViewer upgrade — not a per-event frontend bug.
        if (/\[WebViewer\]/i.test(message) &&
            (/Version Mismatch/i.test(message) || /should be updated to version/i.test(message))) {
          return null;
        }

        // pdf-lib parser recovering from a malformed PDF, e.g.
        //   "Trying to parse invalid object: {"line":..,"column":..,"index":..})"
        //   "Invalid object ref: 268 0 R"
        // Emitted by PDFParser.tryToParseInvalidIndirectObject (console.warn), which
        // captureConsoleIntegration then ships as a warning-level issue. The Plans
        // tab on estimate-list loads CUSTOMER-UPLOADED plan sets, and plenty of real
        // PDFs in the wild have a slightly off xref table. pdf-lib loads with
        // throwOnInvalidObject=false, so it repairs the object and carries on — the
        // upload still succeeds. Nothing here is actionable in app code: the fix
        // would be "the customer's PDF is malformed", and the library already
        // handles it. Drop as third-party recovery noise.
        if (/Invalid object ref:/i.test(message) ||
            /Trying to parse invalid object/i.test(message)) {
          return null;
        }

        // N+1 API call for estimate-sublists has been fixed with batched requests
        // Filter out old production errors until new code is deployed
        if (event.transaction === 'dashboard' &&
            url.includes('/estimate-sublists') &&
            url.includes('estimate_id=')) {
          return null;
        }

        // Skip 401 Unauthorized errors - these are authentication issues handled by axios interceptor
        // The user will be automatically redirected to login page
        if (status == '401' || status == 401 ||
            /API (GET|POST|PUT|PATCH|DELETE) 401\b/.test(message) ||
            message.includes('status code: 401') || message.includes('Unauthorized') ||
            message.includes('Unauthenticated')) {
          return null;
        }

        // Skip Xero API errors - Xero integration is work-in-progress, backend not implemented yet.
        // Catch by request URL, by page URL, and by message text (for console.warn/error captures
        // where the request URL isn't on the event but the log message mentions Xero).
        if (url.includes('/xero/') ||
            event.request?.url?.includes('/xero/') ||
            /\bXero\b/i.test(message)) {
          return null;
        }

        // Skip PostHog analytics errors - third-party service, we can't control their API issues
        if (url.includes('posthog.com') || /posthog/i.test(message)) {
          return null;
        }

        // Skip PDFTron/Apryse WebViewer setCustomData type warnings.
        // PDFTron emits `console.warn("setCustomData received a non-string value for X. It will be automatically stringified.")`
        // for every numeric/boolean value. Auto-stringify is benign, and PDFTron itself
        // also calls setCustomData internally during annotation copy/restore/XFDF import,
        // so even pristine app code can't fully prevent it. Filter at Sentry instead of
        // wrapping all 250+ call sites with String().
        if (/setCustomData received a non-string value/i.test(message)) {
          return null;
        }

        // Skip PDFTron/Apryse WebViewer incremental-download fallback warning.
        // Emitted when WebViewer requests Range headers for partial PDF download but the
        // storage origin doesn't return Content-Range (typical for static file servers).
        // WebViewer transparently falls back to a full download — purely informational.
        if (/Could not use incremental download for url/i.test(message) ||
            /Could not retrieve Content-Range header/i.test(message)) {
          return null;
        }

        // Skip generic "Failed to fetch" network errors - these are temporary connectivity issues
        // already handled by the application's error handling (includes Zapier, external webhooks, etc.)
        if (message.includes('Failed to fetch')) {
          return null;
        }

        // Skip notification fetch errors - these are API errors already handled by the application
        if (message.includes('Failed to fetch notifications from backend')) {
          return null;
        }

        // Skip API fetch failures with fallback to stored data - informational about using cache
        if (message.includes('API fetch failed, using stored data')) {
          return null;
        }

        // Skip Socket.IO transport errors — handled by socket.io's built-in reconnect logic.
        // Match case-insensitively on the message and check both event.request.url
        // (XHR target) and the page URL, since the shared `url` variable above prefers
        // event.contexts.trace.data.url. Also catch by exception type (TransportError)
        // and by breadcrumb category for unhandled rejections that bubble up here.
        const lowerMsg = message.toLowerCase();
        const breadcrumbHasSocket = event.breadcrumbs?.some((b) =>
          (b.message || '').toLowerCase().includes('socket.io') ||
          (b.data?.url || '').includes('socket.io')
        );
        if (
          lowerMsg.includes('socket.io') ||
          lowerMsg.includes('xhr poll error') ||
          lowerMsg.includes('websocket error') ||
          lowerMsg.includes('transport error') ||
          lowerMsg.includes('transport close') ||
          url.includes('socket.io') ||
          event.request?.url?.includes('socket.io') ||
          exceptionType === 'TransportError' ||
          breadcrumbHasSocket
        ) {
          return null;
        }

        // Skip Fabric.js image loading errors - these are network/CORS errors already handled
        if (message.includes('fabric: Error loading data') ||
            message.includes('Error loading data:')) {
          return null;
        }

        // Skip Socket.IO max reconnection attempts - this is expected when connection fails
        if (message.includes('Max reconnection attempts reached')) {
          return null;
        }

        // PDF/DXF preview warnings have been changed from console.warn to console.log
        // Keeping this filter commented out in case regressions occur
        // if (message.includes('No selectedPdf or file - skipping PDF preview') ||
        //     message.includes('No selectedPdf or file - skipping DXF preview')) {
        //   return null;
        // }

        // Skip invalid page request errors - these are validation errors from PDF viewer
        if (message.includes('Invalid page request')) {
          return null;
        }

        // Skip annotation loading warnings - these are expected when annotations aren't loaded yet
        if (message.includes('No annotations data available for unit type drawings') ||
            message.includes('Annotation manager not initialized yet') ||
            message.includes('Need to call loadAnnotations first')) {
          return null;
        }

        // Skip PDF scale warnings - informational only, measurements won't be accurate without scale
        if (message.includes('Page 1 has no scale set') ||
            message.includes('has no scale set, measurements will be inaccurate')) {
          return null;
        }

        // Skip DXF/SVG parsing warnings - informational only, some files may have no renderable objects
        if (message.includes('SVG parsed but produced no renderable objects') ||
            message.includes('[DxfViewer] SVG parsed but produced no renderable objects')) {
          return null;
        }

        // Skip annotation library errors - race conditions when annotation classes aren't loaded yet
        if (message.includes('anotations.Color is not a constructor') ||
            message.includes('annotations.Color is not a constructor')) {
          return null;
        }

        // Skip PDFTron WebViewer measurement warnings - emitted by the library itself when
        // setMeasurementUnits / setAnnotationScale runs against annotations missing the
        // intent-type or measure dictionary. Library-internal noise, not actionable in our code.
        if (message.includes('Unable to set scale to an annotation or a tool which has incorrect IT or no measure dictionary') ||
            message.includes('Unable to set precision to an annotation or a tool which has incorrect IT or no measure dictionary')) {
          return null;
        }

        // Skip form initialization errors on Jobs page - these are transient race conditions
        // during form setup that don't affect functionality
        if (event.transaction === 'jobs' &&
            (message.includes("Cannot read properties of undefined (reading 'work_days')") ||
             message.includes("Cannot read properties of undefined (reading 'job_color')") ||
             message.includes("Cannot read properties of undefined (reading 'update_actual_date')") ||
             message.includes("Cannot read properties of undefined (reading 'actual_completion')") ||
             message.includes("Cannot read properties of undefined (reading 'customers')"))) {
          return null;
        }

        // Skip user authentication race condition errors - these occur when checking permissions
        // before the user store is fully initialized
        if (message.includes("Cannot read properties of undefined (reading 'usertype_lbl')")) {
          return null;
        }

        // Skip session keep-alive failures - informational about expired sessions
        if (message.includes('Session keep-alive failed') ||
            message.includes('session may have expired') ||
            message.includes('Authentication error fetching user info')) {
          return null;
        }

        // Skip generic unhandled promise rejections with no useful error details
        // These are typically race conditions or third-party library issues
        if (message.includes('Event `Event` (type=error) captured as promise rejection')) {
          return null;
        }

        // Skip ReportingObserver deprecation reports we can't act on. The
        // "unload event listeners" warning fires from jQuery, jszip and
        // @syncfusion/ej2-data — none of our own code registers unload
        // listeners, so this is purely third-party deprecation noise.
        if (message.includes('ReportingObserver [deprecation]') &&
            message.includes('Unload event listeners')) {
          return null;
        }

        // Skip ReportingObserver INTERVENTION reports. An intervention is the
        // browser saying "you asked for something and I declined" — most often
        // "Ignored attempt to cancel a touchmove event with cancelable=false",
        // which means a preventDefault() landed on an event that was already
        // committed to scrolling. Nothing broke: the page scrolled, which is
        // what the user was asking for, and the call was a no-op.
        //
        // They arrive at level 'info' with no app frame in the stack — the
        // whole trace is @sentry/core — so there is nothing to act on even
        // when the cause is ours. The causes themselves are better handled at
        // the source, which is why touchmove handlers here guard on
        // `e.cancelable` before preventing; what reaches this point is mostly
        // third party (FullCalendar's interaction plugin on /projects-schedules,
        // the PDF viewer on /estimate-list) where no such guard is available.
        //
        // 'crash' reports still come through — those are the actionable type.
        // BS-WEB-APP-15N.
        if (message.includes('ReportingObserver [intervention]')) {
          return null;
        }

        // Skip Vue Router navigation errors that are already handled
        // These are expected navigation failures (cancelled, duplicated, etc.)
        if (event.tags?.error_type === 'vue_error' &&
            event.tags?.vue_lifecycle === 'errorHandler' &&
            (!message || message === 'No error message') &&
            event.tags?.handled === 'yes') {
          return null;
        }

        // Skip validation warnings from third-party libraries (charts, etc.)
        if (message.includes('Expected: array with [0].scaling property')) {
          return null;
        }

        // Skip response structure validation warnings - these are logged for debugging
        if (message.includes('Invalid response structure')) {
          return null;
        }

        // Skip JSON parsing errors from invalid API responses (HTML error pages, etc.)
        const errorType = event.exception?.values?.[0]?.type || '';
        if (message.includes('is not valid JSON') || message.includes('Unexpected token') ||
            (errorType === 'SyntaxError' && message.includes('JSON'))) {
          return null;
        }

        // Skip DXF/PDF viewer cache warnings - these are informational about scale persistence
        if (message.includes('[DxfStore] No cache found') || message.includes('scale will not persist')) {
          return null;
        }

        // Skip DXF viewer SVG rendering warnings - these are expected for empty/invalid DXF files
        if (message.includes('[DxfViewer] SVG parsed but produced no renderable objects') ||
            message.includes('SVG parsed but produced no renderable objects') ||
            message.includes('No renderable objects found in SVG')) {
          return null;
        }

        // Skip browser notification support warnings - expected on mobile or when notifications disabled
        if (message.includes('Browser does not support notifications')) {
          return null;
        }

        // Skip file rejection warnings - validation messages for file uploads
        if (message.includes('File rejected:')) {
          return null;
        }

        // Skip Google Maps invalid coordinate errors - occurs when lat/lng is missing or invalid
        if (message.includes('setCenter: not a LatLng or LatLngLiteral') ||
            message.includes('in property lat: not a number') ||
            message.includes('in property lng: not a number')) {
          return null;
        }

        // Skip "no data loaded" warnings - informational messages about empty states
        if (message.includes('No plans data loaded')) {
          return null;
        }

        // Skip schedule skipping warnings - informational about deleted/invalid schedules
        if (message.includes('Skipping schedule') && message.includes('may be deleted')) {
          return null;
        }

        // Skip schedule fetching errors - already handled by the application
        if (message.includes('Error fetching linked schedule')) {
          return null;
        }

        // Skip job schedule creation errors - already handled by the application
        if (message.includes('Job schedule create error')) {
          return null;
        }

        // Skip component configuration warnings for unknown options
        if (message.includes('Unknown option')) {
          return null;
        }

        // Skip analytics data generation warnings - informational about generating extra weeks
        if (message.includes('weeks from API, generating more weeks for analytics')) {
          return null;
        }

        // Skip "day not found" warnings - informational about date format mismatches in weekDays
        if (message.includes('Day not found in weekDays') || message.includes('Day not found in weekdays')) {
          return null;
        }

        // Skip property creation errors on primitives - these are data type mismatches already handled
        if (message.includes('Cannot create property') && message.includes('on string')) {
          return null;
        }

        // Skip generic [object Object] console errors - no useful debugging information
        if (message === '[object Object]' || message.trim() === '[object Object]') {
          return null;
        }

        // Skip geocoding fallback warnings - these are informational about using mock data
        if ((message.includes('Could not geocode') || message.includes('geocode city')) &&
            (message.includes('using mock data') || message.includes('mock data'))) {
          return null;
        }

        // Skip legacy weather-API-key fallback warning emitted by older deployed bundles
        if (message.includes('Weather API key not configured')) {
          return null;
        }

        // Skip geocoding "no results" errors - these occur when user enters invalid address
        if (message.includes('No results found for address') ||
            message.includes('Could not geocode')) {
          return null;
        }

        // Moment.js deprecation warnings have been fixed by adding explicit format arrays
        // Keeping this filter commented out in case regressions occur
        // if (message.includes('Deprecation warning') && message.includes('moment')) {
        //   return null;
        // }

        // Skip 404/500/522/523/524 API errors - these are server-side or network issues
        if (message.includes('API GET 404') || message.includes('API POST 404') ||
            message.includes('API PUT 404') || message.includes('API DELETE 404') ||
            message.includes('API GET 500') || message.includes('API POST 500') ||
            message.includes('API PUT 500') || message.includes('API DELETE 500') ||
            message.includes('HTTP Client Error with status code: 500') ||
            message.includes('HTTP Client Error with status code: 404') ||
            message.includes('HTTP Client Error with status code: 522') ||
            message.includes('HTTP Client Error with status code: 523') ||
            message.includes('HTTP Client Error with status code: 524') ||
            (message.includes('showschedule') && message.includes('404'))) {
          return null;
        }

        // Skip version check errors - version.json may not exist in dev/staging, already handled by try-catch
        if (url.includes('version.json') || message.includes('version.json')) {
          return null;
        }

        // Live page info
        event.tags = {
          ...event.tags,
          page_url: window.location.href,
          page_path: window.location.pathname,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          online: String(navigator.onLine),
          orientation: screen.orientation?.type || 'unknown',
          visibility: document.visibilityState,
        };

        // Connection state at time of error
        const conn = (navigator as any).connection;
        const connInfo: Record<string, any> = {
          online: navigator.onLine,
        };
        if (conn) {
          connInfo.effective_type = conn.effectiveType;
          connInfo.downlink = conn.downlink;
          connInfo.rtt = conn.rtt;
          connInfo.save_data = conn.saveData;
        }

        // Performance memory at time of error
        const perf = performance as any;
        const memInfo: Record<string, any> = {};
        if (perf.memory) {
          memInfo.heap_used = `${Math.round(perf.memory.usedJSHeapSize / 1024 / 1024)} MB`;
          memInfo.heap_total = `${Math.round(perf.memory.totalJSHeapSize / 1024 / 1024)} MB`;
          memInfo.heap_limit = `${Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024)} MB`;
          memInfo.heap_usage_pct = `${Math.round((perf.memory.usedJSHeapSize / perf.memory.jsHeapSizeLimit) * 100)}%`;
        }

        event.contexts = {
          ...event.contexts,
          browser_environment: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            languages: navigator.languages?.join(', '),
            user_agent: navigator.userAgent,
            platform: (navigator as any).platform || 'unknown',
            cookie_enabled: navigator.cookieEnabled,
            do_not_track: (navigator as any).doNotTrack,
          },
          screen_info: {
            screen: `${screen.width}x${screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            color_depth: screen.colorDepth,
            pixel_ratio: window.devicePixelRatio,
            orientation: screen.orientation?.type || 'unknown',
          },
          hardware: {
            cpu_cores: navigator.hardwareConcurrency || 'unknown',
            device_memory: (navigator as any).deviceMemory ? `${(navigator as any).deviceMemory} GB` : 'unknown',
            max_touch_points: navigator.maxTouchPoints,
          },
          network_state: connInfo,
          memory_state: memInfo,
          page_info: {
            url: window.location.href,
            path: window.location.pathname,
            referrer: document.referrer || 'direct',
            title: document.title,
            visibility: document.visibilityState,
            dom_elements: document.getElementsByTagName('*').length,
          },
          runtime_state: {
            timestamp: new Date().toISOString(),
            app_uptime_ms: Math.round(performance.now()),
          },
        };

        return event;
      },

      // ── Enrich breadcrumbs ──
      beforeBreadcrumb(breadcrumb) {
        breadcrumb.data = {
          ...breadcrumb.data,
          visibility: document.visibilityState,
        };
        // Enrich click breadcrumbs with element details
        if (breadcrumb.category === 'ui.click') {
          breadcrumb.data = {
            ...breadcrumb.data,
            page_path: window.location.pathname,
          };
        }
        return breadcrumb;
      },
    });

    // Set global context with full browser metadata (persists across all events)
    Sentry.setContext('browser_metadata_full', browserMeta);

    // ── Vue error handler: capture with component context ──
    app.config.errorHandler = (err, instance, info) => {
      Sentry.withScope((scope) => {
        scope.setTag('vue_lifecycle', info);
        scope.setTag('error_type', 'vue_error');
        let componentName = 'Anonymous';
        if (instance) {
          componentName = instance.$options?.name || instance.$options?.__name || 'Anonymous';
          scope.setTag('vue_component', componentName);
          scope.setContext('vue_component_data', {
            component: componentName,
            lifecycle_hook: info,
            route: instance.$route?.fullPath || 'unknown',
            route_name: instance.$route?.name || 'unknown',
            route_params: instance.$route?.params || {},
            route_query: instance.$route?.query || {},
            props: instance.$props ? JSON.stringify(instance.$props).substring(0, 1000) : undefined,
          });
        }

        // Vue hands this handler whatever was thrown, and what gets thrown here
        // is very often NOT an Error: `$axios` is the fetch wrapper, which
        // rejects with the PARSED RESPONSE BODY, so an un-caught API call inside
        // a component handler arrives as `{ success: false, message: '...' }`.
        //
        // captureException() on a plain object cannot title the issue with
        // anything but the object's shape — BS-WEB-APP-15O is literally called
        // "Object captured as exception with keys: message, success", and the
        // one thing that would have explained it, the API's own sentence, was
        // sitting unread in `message`.
        //
        // So read it. fallbackMessage() understands every envelope this API
        // emits, and the result is an issue titled by what actually went wrong.
        // The original value goes to a context so nothing is lost, and the
        // fingerprint is set explicitly because a synthesised Error has a
        // synthesised stack — every one of them is created on this line, so
        // without it Sentry would merge unrelated failures into one group.
        if (err instanceof Error) {
          Sentry.captureException(err);
          return;
        }

        const readable = fallbackMessage(err)?.message;
        let payload: string | undefined;
        try {
          payload = JSON.stringify(err)?.substring(0, 2000);
        } catch {
          payload = String(err).substring(0, 2000);
        }
        scope.setContext('vue_error_payload', {
          type: typeof err,
          value: payload,
          read_as: readable || '(nothing readable)',
        });
        scope.setFingerprint(['vue-error', componentName, info, readable || 'unreadable']);
        Sentry.captureException(new Error(readable || `Non-Error thrown in ${info}`));
      });
      console.error(`[Vue Error] ${info}:`, err);
    };

    // ── Vue warnings (disabled - warnings suppressed globally) ──
    // Warnings are now suppressed in main.ts to reduce console noise
    // if (!import.meta.env.PROD) {
    //   app.config.warnHandler = (msg, instance, trace) => {
    //     Sentry.addBreadcrumb({
    //       category: 'vue.warn',
    //       message: msg,
    //       level: 'warning',
    //       data: { trace: trace?.substring(0, 500) },
    //     });
    //   };
    // }

    // ── Unhandled promise rejections ──
    window.addEventListener('unhandledrejection', (event) => {
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'unhandled_promise_rejection');
        scope.setContext('promise_rejection', {
          reason: String(event.reason),
          stack: event.reason?.stack?.substring(0, 2000),
          url: window.location.href,
          timestamp: new Date().toISOString(),
        });
        Sentry.captureException(event.reason || new Error('Unhandled Promise Rejection'));
      });
    });

    // ── Global JS errors ──
    window.addEventListener('error', (event) => {
      // Only capture resource loading errors (images, scripts, stylesheets)
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        Sentry.addBreadcrumb({
          category: 'resource.error',
          message: `Failed to load: ${(target as any).src || (target as any).href || 'unknown'}`,
          level: 'error',
          data: {
            tag: target.tagName,
            src: (target as any).src || (target as any).href,
          },
        });
      }
    }, true); // capture phase to catch resource errors

    // ── Network online/offline ──
    window.addEventListener('offline', () => {
      Sentry.addBreadcrumb({ category: 'connectivity', message: 'User went offline', level: 'warning' });
      Sentry.setContext('network_state', { online: false, changed_at: new Date().toISOString() });
    });
    window.addEventListener('online', () => {
      Sentry.addBreadcrumb({ category: 'connectivity', message: 'User came back online', level: 'info' });
      Sentry.setContext('network_state', { online: true, changed_at: new Date().toISOString() });
    });

    // ── Page visibility changes (tab switch, minimize) ──
    document.addEventListener('visibilitychange', () => {
      Sentry.addBreadcrumb({
        category: 'app.lifecycle',
        message: `Page visibility: ${document.visibilityState}`,
        level: 'info',
        data: { state: document.visibilityState },
      });
    });

    // ── Page before unload (user leaving) ──
    window.addEventListener('beforeunload', () => {
      Sentry.addBreadcrumb({
        category: 'app.lifecycle',
        message: 'Page unloading (user leaving)',
        level: 'info',
        data: { url: window.location.href, uptime_ms: Math.round(performance.now()) },
      });
    });

    // ── Track long tasks (>50ms main thread blocks) ──
    if ('PerformanceObserver' in window) {
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 100) { // Only log tasks > 100ms
              Sentry.addBreadcrumb({
                category: 'performance',
                message: `Long task detected: ${Math.round(entry.duration)}ms`,
                level: 'warning',
                data: {
                  duration_ms: Math.round(entry.duration),
                  start_time: Math.round(entry.startTime),
                  entry_type: entry.entryType,
                },
              });
            }
          }
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        // PerformanceObserver longtask not supported
      }

      // ── Track layout shifts (CLS) ──
      try {
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutShift = entry as any;
            if (layoutShift.value > 0.1) { // Only significant shifts
              Sentry.addBreadcrumb({
                category: 'performance',
                message: `Layout shift: ${layoutShift.value.toFixed(4)}`,
                level: 'warning',
                data: {
                  value: layoutShift.value,
                  had_recent_input: layoutShift.hadRecentInput,
                },
              });
            }
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // layout-shift not supported
      }
    }

    // ── Create Pinia plugin for store state on errors ──
    sentryPiniaPlugin = createSentryPiniaPlugin({
      attachPiniaState: true,
      addBreadcrumbs: true,
      stateTransformer: (state) => {
        // Remove sensitive data from store state before sending
        const sanitized = { ...state };
        if (sanitized.user) {
          sanitized.user = { ...sanitized.user as any, token: '[REDACTED]', password: '[REDACTED]' };
        }
        return sanitized;
      },
    });

    console.log('Sentry initialized (maximum detail mode)');
  }

  // Initialize PostHog
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST;
  if (posthogKey && posthogKey !== 'your_posthog_key_here') {
    posthog.init(posthogKey, {
      api_host: posthogHost || 'https://us.i.posthog.com',
      // Disable gzip-js compression and bootstrap the key path explicitly to
      // stop "event submitted without an api_key" 401s. Some early-boot /
      // page-unload / recording-batch events were being flushed with the
      // api_key field missing from the JSON body — both flags eliminate that
      // class of edge case at a negligible bandwidth cost.
      disable_compression: true,
      bootstrap: { distinctID: undefined },
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      capture_dead_clicks: true,
      // Disabled: heatmaps capture $autocapture mouse-position events that
      // can batch incorrectly under early-boot conditions and produce the
      // same key-less 401s as $web_vitals. Heatmap data is a low-value
      // analytics extra; turning it off keeps the console clean.
      enable_heatmaps: false,
      enable_recording_console_log: true,
      // Disabled: PostHog's exception capture registers a global error
      // handler that fires before init completes, producing events with a
      // missing api_key field that PostHog then rejects with a 401. Sentry
      // already captures exceptions with richer stack traces and breadcrumbs,
      // so this was duplicate work anyway.
      capture_exceptions: false,
      // Disabled: Web Vitals observers (LCP/FID/CLS/INP) fire within the
      // first few hundred ms of page load — often before posthog-js finishes
      // binding the api_key to its global state. Those events get serialised
      // as bare arrays without the {api_key, batch:[...]} wrapper PostHog
      // requires and end up as 401s. Confirmed by inspecting the failing
      // payload — it was a $web_vitals event with no api_key field.
      // Sentry can capture web vitals too if needed.
      capture_performance: false,
      session_recording: {
        recordCrossOriginIframes: true,
        // Mask all form inputs by default to protect customer data
        // (project costs, client details, financial info, etc.)
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          number: true,
          search: false,
        },
        // Sample 20% of sessions to reduce storage cost while keeping enough
        // data for behavioural analysis. Errors are still captured 100% via Sentry.
        sampleRate: 0.2,
        recordHeaders: false,
        recordBody: false,
      },
      // Strip sensitive properties from every event before it leaves the browser.
      // Strip sensitive properties from every event before it leaves the browser.
      //
      // Do NOT add 'token' here. posthog-js carries the project api_key in
      // properties.token, so denylisting it strips the key out of the event's own
      // payload and every capture comes back 401 "event submitted without an
      // api_key". That is what silently killed all web analytics from 2026-04-20
      // until 2026-08-14: init succeeded, /flags/ succeeded, and every single
      // event was rejected.
      property_denylist: [
        '$ip',
        'password',
        'csrfToken',
        'authorization',
        'creditCard',
        'card_number',
        'cvv',
        'ssn',
        'tfn',
      ],
      rageclick: true,
      persistence: 'localStorage+cookie',
      // Tag every event with the build mode so we can distinguish
      // staging vs production traffic inside PostHog.
      loaded: (ph) => {
        ph.register({
          app_environment: mode,
          app_name: 'builtsimple-web',
        });
      },
    });
    console.log(`PostHog initialized (${mode} mode)`);
  }
}

/**
 * Optional user profile properties that get attached to PostHog and Sentry
 * when a user logs in. Anything you pass here becomes a "person property" in
 * PostHog and is queryable across every event that user generates.
 *
 * Pass whatever the backend returns on the user object — only defined fields
 * are sent. Add new fields here when the API starts returning them.
 */
export interface UserProfileProperties {
  company_name?: string;
  company_id?: string | number;
  user_role?: string;
  subscription_tier?: string;
  subscription_status?: string;
  signup_date?: string;
  is_admin?: boolean;
  is_owner?: boolean;
  team_size?: number;
  trial_ends_at?: string;
  preferred_currency?: string;
  country?: string;
}

export function identifySentryUser(
  userId: string,
  email?: string,
  username?: string,
  profile: UserProfileProperties = {}
) {
  Sentry.setUser({
    id: userId,
    email: email || undefined,
    username: username || undefined,
  });
  Sentry.addBreadcrumb({
    category: 'auth',
    message: `User identified: ${email || username || userId}`,
    level: 'info',
  });
  Sentry.setContext('user_profile', {
    id: userId,
    email: email || undefined,
    username: username || undefined,
    ...profile,
    identified_at: new Date().toISOString(),
  });

  try {
    // Strip undefined values so PostHog person properties stay clean
    const personProps: Record<string, any> = {
      email: email || undefined,
      name: username || undefined,
      identified_at: new Date().toISOString(),
    };
    Object.entries(profile).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        personProps[key] = value;
      }
    });
    Object.keys(personProps).forEach((k) => {
      if (personProps[k] === undefined) delete personProps[k];
    });

    posthog.identify(userId, personProps);

    // Company group analytics: lets PostHog aggregate events per company
    // (funnels/retention by builder org, not just by individual user).
    if (profile.company_id) {
      posthog.group('company', String(profile.company_id), {
        name: profile.company_name || undefined,
        signup_date: profile.signup_date || undefined,
        subscription_tier: profile.subscription_tier || undefined,
        subscription_status: profile.subscription_status || undefined,
        team_size: profile.team_size || undefined,
        trial_ends_at: profile.trial_ends_at || undefined,
        country: profile.country || undefined,
      });
    }
  } catch (e) {
    // PostHog may not be initialized
  }
}

export function resetSentryUser() {
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'User logged out',
    level: 'info',
  });
  Sentry.setUser(null);
  Sentry.setContext('user_profile', null);

  try {
    posthog.reset();
  } catch (e) {
    // PostHog may not be initialized
  }
}

/**
 * Add a custom breadcrumb for tracking user actions
 */
export function addSentryBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, any>,
  level: 'info' | 'warning' | 'error' = 'info'
) {
  Sentry.addBreadcrumb({ category, message, data, level });
}

/**
 * Track a user action (button click, form submit, modal open, etc.)
 */
export function trackUserAction(action: string, details?: Record<string, any>) {
  Sentry.addBreadcrumb({
    category: 'user.action',
    message: action,
    data: { ...details, page: window.location.pathname, timestamp: new Date().toISOString() },
    level: 'info',
  });
}

/**
 * Capture an API error with full request/response context
 */
export function captureApiError(
  url: string,
  method: string,
  status: number,
  responseData: any,
  requestBody?: any
) {
  // Get connection state at time of error
  const conn = (navigator as any).connection;
  const networkState: Record<string, any> = { online: navigator.onLine };
  if (conn) {
    networkState.effective_type = conn.effectiveType;
    networkState.downlink = conn.downlink;
    networkState.rtt = conn.rtt;
  }

  // Skip 422 errors (used for validation responses like invalid login,
  // password, etc.) and 401s (auth handled by axios interceptor — user
  // is redirected to login automatically; surfacing in Sentry is noise).
  // 413 joins them: an oversize upload is the user's file, not our bug. The
  // API returns a message naming the size and the cap, and the caller shows it.
  //
  // 409 is the same kind of answer one step along: the request was fine, it
  // collides with what is already stored — leave that overlaps existing leave,
  // a work type that already exists, a timesheet week already adjusted. The API
  // names the collision and the caller shows it, so the user can change the
  // input and carry on. Reporting it filed a High-priority unhandled issue
  // every time somebody picked the wrong dates (BS-WEB-APP-17A).
  if (status === 422 || status === 401 || status === 413 || status === 409) {
    return;
  }

  // Best-effort endpoints whose failures are already handled by the caller
  // and don't affect the user. Reporting them to Sentry just adds noise when
  // shared infra (sanctum/cache/DB) has a transient blip.
  if (url.includes('/session-ping')) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error');
    scope.setTag('api_status', String(status));
    scope.setTag('api_method', method);
    scope.setTag('api_endpoint', url);
    scope.setContext('api_request', {
      url,
      method,
      body: requestBody ? JSON.stringify(requestBody).substring(0, 4000) : undefined,
      timestamp: new Date().toISOString(),
    });
    scope.setContext('api_response', {
      status,
      data: responseData !== undefined ? JSON.stringify(responseData).substring(0, 4000) : undefined,
    });
    scope.setContext('network_at_error', networkState);
    scope.setFingerprint([method, url, String(status)]);
    Sentry.captureException(
      new Error(`API ${method} ${status}: ${url}`)
    );
  });
}
